"use client"
import type React from "react"
import { Link } from "react-router-dom"

interface Announcement {
  id: number
  title: string
  description: string
  created_at: string
  type: "info" | "warning" | "danger" | string
}

interface AnnouncementCardProps {
  announcements: Announcement[]
  permissions: string[]
}

const AnnouncementCard: React.FC<AnnouncementCardProps> = ({ announcements, permissions }) => {
  // Sort announcements by date (newest first) and take only first 5
  const sortedAnnouncements = [...announcements]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5)

  // Format date to be more readable
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  // Get priority styling
  const getPriorityStyles = (type: string) => {
    switch (type) {
      case "danger":
        return {
          bg: "bg-red-100 dark:bg-red-900/30",
          text: "text-red-800 dark:text-red-300",
          icon: "fas fa-exclamation-circle text-red-500 dark:text-red-400",
        }
      case "info":
        return {
          bg: "bg-blue-100 dark:bg-blue-900/30",
          text: "text-blue-800 dark:text-blue-300",
          icon: "fas fa-info-circle text-blue-500 dark:text-blue-400",
        }
      case "warning":
        return {
          bg: "bg-yellow-100 dark:bg-yellow-800",
          text: "text-yellow-800 dark:text-yellow-300",
          icon: "fas fa-bell text-yellow-500 dark:text-yellow-400",
        }
      default:
        return {
          bg: "bg-blue-100 dark:bg-blue-900/30",
          text: "text-blue-800 dark:text-blue-300",
          icon: "fas fa-info-circle text-blue-500 dark:text-blue-400",
        }
    }
  }

  // Check if there are more announcements than what we're showing
  const hasMoreAnnouncements = announcements.length > 5

  return (
    <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Announcements</h2>
          {permissions && permissions.includes("bulletin.add_announcement") && (
            <Link
              to="/bulletin/announcements/create/"
              className="w-8 h-8 flex items-center justify-center text-white bg-brand-500 hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 rounded-full transition-colors duration-200 shadow-sm mb-3"
            >
              <i className="fas fa-plus text-sm"></i>
            </Link>
          )}
        </div>

        <div className="space-y-4">
          {sortedAnnouncements.map((announcement) => {
            const priorityStyles = getPriorityStyles(announcement.type)
            return (
              <div
                key={announcement.id}
                className="border border-gray-200 dark:border-navy-600 rounded-lg overflow-hidden"
              >
                <div
                  className={`${priorityStyles.bg} px-4 py-2 flex items-center justify-between border-b border-gray-200 dark:border-navy-600`}
                >
                  <div className="flex items-center">
                    <i className={`${priorityStyles.icon} mr-2`}></i>
                    <h4 className={`font-medium ${priorityStyles.text}`}>{announcement.title}</h4>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(announcement.created_at)}
                  </span>
                </div>
                <div className="p-4 bg-white dark:bg-navy-800">
                  <p className="text-sm text-gray-600 dark:text-gray-300">{announcement.description}</p>
                </div>
              </div>
            )
          })}
        </div>

        {sortedAnnouncements.length === 0 && (
          <div className="text-center py-8 bg-gray-50 dark:bg-navy-900 rounded-lg">
            <i className="fas fa-bullhorn text-gray-400 text-3xl mb-2"></i>
            <p className="text-gray-500 dark:text-gray-400">No announcements at this time</p>
          </div>
        )}

        {/* View All Announcements Button */}
        {hasMoreAnnouncements && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-navy-700">
            <Link
              to="/announcements/"
              className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded-lg transition-colors"
            >
              <i className="fas fa-bullhorn mr-2"></i>
              View All Announcements
              {hasMoreAnnouncements && (
                <span className="ml-2 px-2 py-0.5 bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 text-xs rounded-full">
                  +{announcements.length - 5} more
                </span>
              )}
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default AnnouncementCard